/**
 * Порт платёжных провайдеров (граница «ядро ↔ платёжный модуль»).
 *
 * Ядро не знает, кто именно обслуживает оплату (СБП NewPay, Click, Payme):
 * оно просит провайдера создать QR и получает исход через UI-резолвер.
 */

/** Идентификаторы способов оплаты, которые понимают провайдеры. */
export const PAYMENT_METHODS = {
  sbp: 'sbp',
  uzClick: 'uz-click',
  uzPayme: 'uz-payme'
} as const

export type PaymentMethodId = (typeof PAYMENT_METHODS)[keyof typeof PAYMENT_METHODS]

/** Запрос на создание оплаты. */
export interface PaymentQrRequest {
  receiptId: string
  /** Сумма в валюте магазина. */
  amount: number
  /** Способ оплаты: `sbp`, `uz-click`, `uz-payme` и т.п. */
  method: string
}

/** Готовый QR/диплинк для показа покупателю. */
export interface PaymentQr {
  /** Идентификатор провайдера (`ru-sbp`, `uz-pay`). */
  providerId: string
  /** Идентификатор операции у агрегатора. */
  operationId: string
  /** Строка для QR (ссылка/диплинк). */
  payload: string
  /** Готовый SVG, если провайдер умеет рисовать сам. */
  svg?: string
}

/** Исход оплаты. */
export type PaymentOutcome =
  | { status: 'succeeded' }
  | { status: 'failed'; reason: string }
  | { status: 'cancelled' };

/**
 * UI-резолвер оплаты: показывает модалку с QR и ждёт подтверждения/статуса
 * (в бою — WebSocket агрегатора, в демо — кнопка «Оплачено»).
 */
export type PaymentUiResolver = (req: {
  request: PaymentQrRequest
  qr: PaymentQr
}) => Promise<PaymentOutcome>;

/** Платёжный провайдер конкретной системы. */
export interface PaymentProvider {
  readonly id: string
  /** Поддерживает ли провайдер способ оплаты. */
  supports(method: string): boolean
  /** Создать операцию и вернуть QR/диплинк. */
  createQr(req: PaymentQrRequest): Promise<PaymentQr>
}
